import { useCallback, useRef } from 'react';

import { scrollSmoothly } from 'app/services/scroll.service';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import isSafariOrIOS from 'app/shared/helpers/isSafariOrIos';

const getElementTopPosition = (element: Element) => {
  const bound = element.getBoundingClientRect();
  const margin = 20;

  return bound.top - document.body.getBoundingClientRect().top - margin;
};

const getDistanceToBottom = (element: Element) => {
  const clientHeight =
    window.innerHeight || document.documentElement.clientHeight;

  return clientHeight - element.getBoundingClientRect().bottom;
};

const getDistanceToTop = (element: Element) => {
  const menuHeightWithMargin = isMobileOrTablet() ? 120 : 0;

  return element.getBoundingClientRect().top - menuHeightWithMargin;
};

const scrollToOptionSelect = () => {
  const multipleSelectComponent = document.querySelector<HTMLElement>(
    '#autocomplete'
  );
  if (!multipleSelectComponent) {
    return;
  }
  let multipleSelectComponentTopPosition = getElementTopPosition(
    multipleSelectComponent
  );

  const additionalMarginToTop = 70;
  scrollSmoothly(multipleSelectComponentTopPosition - additionalMarginToTop);
};

const canScrollMultipleSelectFromBelow = () => {
  const multipleSelectComponent = document.querySelector<HTMLElement>(
    '#autocomplete'
  );
  if (!multipleSelectComponent) {
    return false;
  }
  const distanceToBottom = getDistanceToBottom(multipleSelectComponent);
  const distanceToTop = getDistanceToTop(multipleSelectComponent);
  const distanceToTopAfterAutoScroll = 67;
  return distanceToTop > distanceToTopAfterAutoScroll && isMobileOrTablet()
    ? true
    : distanceToBottom < 270;
};

const KEYBOARD_DETECTION_TIMEOUT = 1000;
const KEYBOARD_FALLBACK_DELAY = 300;

const scrollFieldUnderNavbar = (element: HTMLElement) => {
  const fieldTop = window.pageYOffset + element.getBoundingClientRect().top;
  scrollSmoothly(fieldTop);
};

export function useMultipleSelectScroll() {
  const cancelKeyboardScroll = useRef<(() => void) | null>(null);
  const isKeyboardScrollArmed = useRef(false);

  const handleScroll = (e: React.MouseEvent) => {
    if (e.currentTarget === null) {
      return;
    }

    const distanceToBottom = getDistanceToBottom(e.currentTarget);
    const distanceToTop = getDistanceToTop(e.currentTarget);

    const canScrollFromBelow = canScrollMultipleSelectFromBelow();
    if (canScrollFromBelow) {
      scrollToOptionSelect();
    }

    const canScrollFromAbove = distanceToBottom >= 350 && distanceToTop <= 0;

    if (canScrollFromAbove) {
      scrollToOptionSelect();
    }
  };

  const scrollFieldAboveKeyboard = useCallback(
    (element: HTMLElement | null) => {
      if (!element || !isMobileOrTablet() || isSafariOrIOS()) {
        return;
      }

      if (isKeyboardScrollArmed.current) {
        return;
      }
      isKeyboardScrollArmed.current = true;

      const viewport = window.visualViewport;
      if (!viewport) {
        const timeoutId = window.setTimeout(
          () => scrollFieldUnderNavbar(element),
          KEYBOARD_FALLBACK_DELAY
        );
        cancelKeyboardScroll.current = () => window.clearTimeout(timeoutId);
        return;
      }

      const finish = (shouldScroll: boolean) => {
        viewport.removeEventListener('resize', onResize);
        window.clearTimeout(timeoutId);
        cancelKeyboardScroll.current = null;
        if (shouldScroll) {
          scrollFieldUnderNavbar(element);
        }
      };

      const onResize = () => finish(true);
      const timeoutId = window.setTimeout(
        () => finish(false),
        KEYBOARD_DETECTION_TIMEOUT
      );

      viewport.addEventListener('resize', onResize);
      cancelKeyboardScroll.current = () => finish(false);
    },
    []
  );

  const resetKeyboardScroll = useCallback(() => {
    cancelKeyboardScroll.current?.();
    isKeyboardScrollArmed.current = false;
  }, []);

  return {
    handleScroll,
    scrollFieldAboveKeyboard,
    resetKeyboardScroll,
  };
}
